<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Info } from "@lucide/vue";
import Combobox from "@/components/ui/Combobox.vue";
import ColumnMatching from "@/components/import/ColumnMatching.vue";
import ImportFileCard from "@/components/import/ImportFileCard.vue";
import ImportSection from "@/components/import/ImportSection.vue";
import ImportSteps from "@/components/import/ImportSteps.vue";
import ImportSummary from "@/components/import/ImportSummary.vue";
import { normalize, targetField } from "@/components/import/columnMatching";
import type { ColumnMatchingValue, FileColumn, ImportedFile, ImportRequirement, TargetField } from "@/components/import/types";
import SegmentedControl from "@/components/ui/SegmentedControl.vue";
import Toggle from "@/components/ui/Toggle.vue";

enum ImportType {
  Both = "both",
  Product = "product",
  Equipment = "equipment"
}

interface InitialData {
  type?: string;
  update_only?: boolean;
  unique_key?: string;
  product_type?: number | null;
  column_matching?: Record<string, string | Record<string, string> | null>;
  equipment_status?: string | null;
  equipment_health_state?: string | null;
  park?: number | null;
}

interface TypeOption {
  value: string;
  label: string;
}

interface ProductType {
  id: number;
  name: string;
}

interface Locale {
  value: string;
  label: string;
  emoji: string;
}

interface StatusOption {
  value: string;
  label: string;
}

interface Park {
  id: number;
  text: string;
}

interface UniqueKey {
  value: string;
  label: string;
}

const props = defineProps<{
  types: TypeOption[];
  initialData: InitialData;
  productTypes: ProductType[];
  productFields: Record<string, string>;
  fileHeadingOptions: FileColumn[];
  translatableModelFields: Record<string, string>;
  locales: Locale[];
  equipmentFields: Record<string, string>;
  equipmentStatuses: StatusOption[] | null;
  equipmentHealthStates: StatusOption[] | null;
  parks: Park[];
  uniqueKeys: UniqueKey[];
  file: ImportedFile;
  prefilledFromLastImport: boolean;
}>();

const { t } = useI18n();

const formData = reactive({
  type: (props.initialData.type as ImportType | undefined) ?? (props.types.length === 1 ? props.types[0].value as ImportType : undefined),
  update_only: Boolean(Number(props.initialData.update_only ?? 0)),
  unique_key: props.initialData.unique_key ?? undefined,
  product_type: props.initialData.product_type ?? undefined,
  equipment_status: props.initialData.equipment_status ?? undefined,
  equipment_health_state: props.initialData.equipment_health_state ?? undefined,
  park: props.initialData.park ?? undefined,
});

const showProductFields = computed(() =>
  formData.type === ImportType.Product || formData.type === ImportType.Both
);

const showEquipmentFields = computed(() =>
  formData.type === ImportType.Equipment || formData.type === ImportType.Both
);

const filteredUniqueKeys = computed(() => {
  if (showProductFields.value || (formData.type === ImportType.Equipment && !formData.update_only)) {
    return props.uniqueKeys.filter(uk => uk.value in props.productFields);
  }
  if (formData.type === ImportType.Equipment) {
    return props.uniqueKeys.filter(uk => uk.value in props.equipmentFields);
  }
  return [];
});

watch(filteredUniqueKeys, (keys) => {
  if (!keys.some(key => key.value === formData.unique_key)) {
    formData.unique_key = keys.length === 1 ? keys[0].value : undefined;
  }
}, { immediate: true });

const fieldAliases: Record<string, string[]> = {
  product_reference: ["ref", "modelreference", "modelref"],
  product_reference_aliases: ["aliases", "referencealiases"],
  product_name: ["name", "nom", "model", "modele", "modelname", "nommodele"],
  product_year: ["year", "annee", "millesime", "modelyear"],
  product_description: ["description"],
  product_link: ["link", "url", "lien"],
  equipment_barcode: ["barcode", "qrcode", "codebarre", "codebarres", "ean"],
  equipment_size: ["size", "taille"],
  equipment_color: ["color", "colour", "couleur", "coloris"],
  equipment_serial_number: ["serial", "serialnumber", "numerodeserie", "sn"],
  equipment_reference: ["reference", "ref", "sku"],
  equipment_comment: ["comment", "comments", "commentaire", "commentaires", "note", "notes"],
};

const unprefixed = (key: string): string => key.replace(/^(product|equipment)_/, "");

const makeField = (key: string, label: string, group: string, inputName: string, aliases: string[]): TargetField =>
  targetField(key, label, group, inputName, aliases, unprefixed(key));

const fieldGroup = (key: string): string => {
  if (key.startsWith("product_metafield_")) return t("Model custom field");
  if (key.startsWith("equipment_metafield_")) return t("Item custom field");
  return key.startsWith("product_") ? t("Model") : t("Item");
};

const availableFields = computed<TargetField[]>(() => {
  const fields: TargetField[] = [];

  // Importing items alone only needs the model field items are attached by.
  Object.entries(props.productFields)
    .filter(([key]) => showProductFields.value || key === formData.unique_key)
    .forEach(([key, label]) => fields.push(
      makeField(key, label, fieldGroup(key), `column_matching[${key}]`, fieldAliases[key] ?? [])
    ));

  if (showProductFields.value) {
    Object.entries(props.translatableModelFields).forEach(([key, label]) => {
      props.locales.forEach((locale) => {
        const aliases = (fieldAliases[key] ?? [unprefixed(key)]).flatMap(alias => [
          normalize(alias + locale.value),
          normalize(alias + locale.label),
        ]);
        fields.push(makeField(
          `${key}.${locale.value}`,
          `${label} · ${locale.label} ${locale.emoji}`,
          fieldGroup(key),
          `column_matching[${key}][${locale.value}]`,
          aliases,
        ));
      });
    });
  }

  if (showEquipmentFields.value) {
    Object.entries(props.equipmentFields).forEach(([key, label]) => fields.push(
      makeField(key, label, fieldGroup(key), `column_matching[${key}]`, fieldAliases[key] ?? [])
    ));
  }

  return fields;
});

const columns = props.fileHeadingOptions;

/** The matching of the last import, flattened to the keys of the target fields. */
const columnByField = ref<ColumnMatchingValue>(Object.fromEntries(
  Object.entries(props.initialData.column_matching ?? {}).flatMap(([field, value]) => {
    if (typeof value === "string") return [[field, value]];
    if (value && typeof value === "object") {
      return Object.entries(value).map(([locale, column]) => [`${field}.${locale}`, column]);
    }
    return [];
  })
));

const isMapped = (key: string): boolean => key in columnByField.value;

// Mirrors ProcessProductEquipmentListFileRequest, so the import cannot be sent to fail validation.
const requirements = computed<ImportRequirement[]>(() => {
  const items: ImportRequirement[] = [
    { label: t("What you are importing"), met: !!formData.type },
    { label: t("How existing models or items are identified"), met: !!formData.unique_key },
  ];

  if (formData.unique_key) {
    const label = props.uniqueKeys.find(key => key.value === formData.unique_key)?.label ?? formData.unique_key;
    items.push({ label: t("A column for {field}", { field: label }), met: isMapped(formData.unique_key) });
  }

  if (showProductFields.value) {
    ["product_name", "product_year"]
      .filter(key => key !== formData.unique_key && key in props.productFields)
      .forEach(key => items.push({
        label: t("A column for {field}", { field: props.productFields[key] }),
        met: isMapped(key),
      }));
    items.push({ label: t("Model category"), met: !!formData.product_type });
  }

  if (showEquipmentFields.value) {
    items.push({ label: t("Park for the new items"), met: !!formData.park });
  }

  return items;
});

const barcodesGenerated = computed(() =>
  showEquipmentFields.value && "equipment_barcode" in props.equipmentFields && !isMapped("equipment_barcode")
);
</script>

<template>
  <div class="tw:flex tw:flex-col tw:gap-6">
    <ImportSteps :current="1" />

    <ImportFileCard :file="file" :columns-count="columns.length" />

    <div class="tw:grid tw:gap-6 tw:lg:grid-cols-[minmax(0,1fr)_20rem] tw:items-start">
      <div class="tw:space-y-6 tw:min-w-0">
        <!-- Settings -->
        <ImportSection :title="$t('What are you importing?')">
          <div class="tw:space-y-5 tw:px-5 tw:py-4">
            <div v-if="types.length > 1">
              <div class="tw:mb-2 tw:text-sm tw:font-medium">{{ $t("Content of the file") }}</div>
              <SegmentedControl v-model="formData.type" :options="types" />
            </div>
            <input type="hidden" name="type" :value="formData.type ?? ''">

            <div>
              <div class="tw:mb-2 tw:text-sm tw:font-medium">{{ $t("Recognize existing models or items by") }}</div>
              <SegmentedControl
                v-if="filteredUniqueKeys.length > 0"
                v-model="formData.unique_key"
                :options="filteredUniqueKeys"
              />
              <p v-else class="tw:m-0 tw:text-sm tw:text-zinc-500">{{ $t("Choose what you are importing first.") }}</p>
              <p class="tw:mt-2 tw:mb-0 tw:text-sm tw:text-zinc-500">
                {{ $t("Rows sharing the same identifier are ignored, so make sure each one is unique in your file.") }}
              </p>
              <input type="hidden" name="unique_key" :value="formData.unique_key ?? ''">
            </div>

            <div class="tw:flex tw:items-start tw:gap-3">
              <Toggle id="formImportUpdateOnly" v-model="formData.update_only" class="tw:mt-0.5" />
              <label for="formImportUpdateOnly" class="tw:mb-0 tw:cursor-pointer tw:font-normal">
                <span class="tw:block tw:text-sm tw:font-medium">{{ $t("Update only") }}</span>
                <span class="tw:block tw:text-sm tw:text-zinc-500">
                  {{ $t("If checked, only existing models/items will be updated, new ones will be ignored") }}
                </span>
              </label>
              <input v-if="formData.update_only" type="hidden" name="update_only" value="1">
            </div>
          </div>
        </ImportSection>

        <ColumnMatching
          v-model="columnByField"
          :columns="columns"
          :fields="availableFields"
          :identifier-key="formData.unique_key"
          :disabled="!formData.type"
          :prefilled="prefilledFromLastImport"
        >
          <template #disabled>
            {{ $t("Choose what you are importing to see the fields your columns can go into.") }}
          </template>
        </ColumnMatching>

        <!-- Values shared by every row -->
        <ImportSection
          v-if="formData.type"
          :title="$t('Applied to every row')"
          :description="$t('These values do not come from your file.')"
        >
          <div class="tw:grid tw:gap-5 tw:px-5 tw:py-4">
            <div v-if="showProductFields">
              <label for="formImportProductType" class="tw:text-sm tw:font-medium">{{ $t("Model category") }}</label>
              <Combobox
                id="formImportProductType"
                name="product_type"
                v-model="formData.product_type"
                :options="productTypes.map((productType) => ({ value: productType.id, label: productType.name }))"
                :fullwidth="true"
              />
            </div>

            <template v-if="showEquipmentFields">
              <div>
                <label for="formImportPark" class="tw:text-sm tw:font-medium">{{ $t("Add items to park") }}</label>
                <Combobox
                  id="formImportPark"
                  name="park"
                  v-model="formData.park"
                  :options="parks.map((park) => ({ value: park.id, label: park.text }))"
                  :fullwidth="true"
                />
              </div>

              <div v-if="equipmentStatuses">
                <label for="formImportEquipmentStatus" class="tw:text-sm tw:font-medium">{{ $t("Item status") }}</label>
                <Combobox
                  id="formImportEquipmentStatus"
                  name="equipment_status"
                  v-model="formData.equipment_status"
                  :options="equipmentStatuses.map((status) => ({ value: status.value, label: status.label }))"
                  :fullwidth="true"
                />
              </div>

              <div v-if="equipmentHealthStates">
                <label for="formImportEquipmentHealthState" class="tw:text-sm tw:font-medium">{{ $t("Item health state") }}</label>
                <Combobox
                  id="formImportEquipmentHealthState"
                  name="equipment_health_state"
                  v-model="formData.equipment_health_state"
                  :options="equipmentHealthStates.map((state) => ({ value: state.value, label: state.label }))"
                  :fullwidth="true"
                />
              </div>
            </template>
          </div>
        </ImportSection>
      </div>

      <ImportSummary :requirements="requirements" :rows="file.dataRows">
        <p v-if="barcodesGenerated" class="tw:mt-4 tw:mb-0 tw:flex tw:items-start tw:gap-2 tw:rounded-lg tw:bg-zinc-50 tw:p-3 tw:text-sm tw:text-zinc-600">
          <Info class="tw:mt-0.5 tw:size-4 tw:shrink-0" />
          {{ $t("No column for Barcode / QR Code: a barcode will be generated for each new item.") }}
        </p>
      </ImportSummary>
    </div>
  </div>
</template>
